import type { SupabaseClient } from "@supabase/supabase-js"
import type { Database } from "../types/database"
import type { Species } from "../types"

/**
 * A failed enrichment write. Keeps the PostgREST `code` and HTTP `status`
 * alongside the message so callers that classify failures (the mobile
 * upload connector: transient → retry, permanent → park) can still tell
 * a dropped connection from a genuine rejection.
 */
export class SpeciesEnrichmentError extends Error {
  code?: string
  status: number

  constructor(
    message: string,
    { code, status }: { code?: string; status: number },
  ) {
    super(message)
    this.name = "SpeciesEnrichmentError"
    this.code = code
    this.status = status
  }
}

/**
 * Attach an ALA GUID to a species that doesn't have one yet.
 *
 * A species can end up without one when it was first recorded offline as a
 * "custom" species (typed name, no network to match against ALA). Later,
 * the same species may be identified from ALA — either by another upload
 * racing in with a matching name, or by a user linking it manually. Either
 * way this fills in the gap rather than leaving the identification stuck on
 * whichever record happened to be created first.
 *
 * Never overwrites an existing ala_guid: the `.is("ala_guid", null)` guard
 * makes this safe to call speculatively, and the update matches zero rows
 * (returning `null`, not an error) if the species already has one.
 */
export async function enrichSpeciesWithAlaGuid(
  client: SupabaseClient<Database>,
  { speciesId, alaGuid }: { speciesId: string; alaGuid: string },
): Promise<Species | null> {
  const { data, error, status } = await client
    .from("species")
    .update({ ala_guid: alaGuid })
    .eq("id", speciesId)
    .is("ala_guid", null)
    .select("*")
    .maybeSingle()

  if (error) {
    throw new SpeciesEnrichmentError(error.message, {
      code: error.code || undefined,
      status,
    })
  }
  return (data as Species | null) ?? null
}
